/**
 * Store a string or number value in sessionStorage
 * @param key Storage key
 * @param value String or number to store
 * @returns true if successful, false if storage failed
 */
export const setSessionItem = (
    key: string,
    value: string | number
): boolean => {
    try {
        window.sessionStorage.setItem(key, value.toString())
        return true
    } catch (e) {
        console.error(`Failed to set sessionStorage item "${key}":`, e)
        return false
    }
}

/**
 * Store a JSON object in sessionStorage
 * @param key Storage key
 * @param json Object to store
 * @returns true if successful, false if storage or serialization failed
 */
export const setSessionJSON = (key: string, json: object): boolean => {
    try {
        const jsonValue = JSON.stringify(json)
        window.sessionStorage.setItem(key, jsonValue)
        return true
    } catch (e) {
        console.error(`Failed to set sessionStorage JSON "${key}":`, e)
        return false
    }
}

/**
 * Get a string value from sessionStorage
 * @param key Storage key
 * @returns String value or null if not found or error
 */
export const getSessionItem = (key: string): string | null => {
    try {
        return window.sessionStorage.getItem(key)
    } catch (e) {
        console.error(`Failed to get sessionStorage item "${key}":`, e)
        return null
    }
}

/**
 * Get and parse a JSON object from sessionStorage
 * @param key Storage key
 * @returns Parsed object or null if not found, invalid JSON, or error
 */
export const getSessionJSON = (key: string): object | null => {
    try {
        const value = window.sessionStorage.getItem(key)
        if (!value) return null
        return JSON.parse(value)
    } catch (e) {
        console.error(`Failed to get sessionStorage JSON "${key}":`, e)
        return null
    }
}

/**
 * Remove an item from sessionStorage
 * @param key Storage key
 * @returns true if successful, false if error
 */
export const removeSessionItem = (key: string): boolean => {
    try {
        window.sessionStorage.removeItem(key)
        return true
    } catch (e) {
        console.error(`Failed to remove sessionStorage item "${key}":`, e)
        return false
    }
}

/**
 * Clear all sessionStorage data
 * @returns true if successful, false if error
 */
export const clearSession = (): boolean => {
    try {
        window.sessionStorage.clear()
        return true
    } catch (e) {
        console.error('Failed to clear sessionStorage:', e)
        return false
    }
}
